import React from 'react';
import PropTypes from 'prop-types';

/**
 * Dauerhaft im DOM stehende, visuell versteckte Live-Region für
 * Status-Ansagen von Screenreadern (WCAG 4.1.3 Status Messages).
 *
 * Hintergrund: Assistive Technologien (v.a. NVDA/JAWS mit Firefox, VoiceOver
 * praktisch immer) setzen voraus, dass eine Live-Region BEREITS im
 * Accessibility-Tree registriert ist, BEVOR sich ihr Inhalt ändert. Ein
 * komplett neu eingefügter, bereits gefüllter Knoten (z.B.
 * `{show && <div aria-live="polite">Text</div>}`) wird als gewöhnliche
 * DOM-Einfügung behandelt und NICHT vorgelesen — genau der Fehler, den diese
 * Komponente vermeidet.
 *
 * WICHTIG für Aufrufer: Diese Komponente MUSS unabhängig von der
 * Sichtbarkeit des zugehörigen visuellen Inhalts gemountet werden (also
 * z.B. außerhalb eines `if (!show) return null;`-Zweigs) — nur ihr
 * `text`-Prop darf sich ändern. Wird sie selbst erst bedingt gemountet,
 * tritt derselbe Timing-Fehler nur eine Ebene tiefer wieder auf.
 *
 * Bewusst NUR `role="status"` ohne zusätzliches `aria-live="polite"`/
 * `aria-atomic="true"`: role="status" impliziert beide Werte bereits
 * (redundante Attribute müssten sonst doppelt gepflegt werden) — dasselbe
 * Muster wie die bestehenden Live-Regionen in SignaturePad.js
 * (`{padId}-status`) und StatistikPage.js (`#statistik-sort-status`).
 *
 * Interaktive Inhalte (Buttons, Links) gehören NICHT in diese Komponente:
 * manche AT lesen bei jedem Live-Update den kompletten Regioninhalt erneut
 * vor, was einen gerade fokussierten Button unterbrechen würde. Solche
 * Elemente werden als Geschwister außerhalb der Live-Region gerendert.
 *
 * @param {object} props
 * @param {string} props.id   - eindeutige, pro Verwendungsstelle eigene ID
 * @param {string} [props.text] - anzusagender Text (leer = keine Ansage)
 */
// Default-Wert bewusst als Parameter-Default statt über defaultProps: React 19
// wertet defaultProps bei Funktionskomponenten nicht mehr aus (entfernt), der
// Default würde dort also stillschweigend nicht greifen.
const PoliteAnnouncer = ({ id, text = '' }) => (
  <div id={id} className="visually-hidden" role="status">
    {text}
  </div>
);

PoliteAnnouncer.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.string
};

export default PoliteAnnouncer;
